/**
 * Auth0-style "Getting Started" progress, INFERRED from real tenant data.
 *
 * Rather than track a brittle server-side checklist, each step's completion is
 * derived from a cheap signal the dashboard already has:
 *
 *  - **wallet**    — a connected browser wallet (`@authe/web3` inventory);
 *  - **team**      — at least one team exists (`GET /teams`);
 *  - **contracts** — at least one stored ABI (`GET /abis`);
 *  - **catalog**   — at least one entity (catalog-mgmt, via the shared
 *    {@link useCatalogApi} port — same GraphQL fast path + `entity → entities`
 *    routing as the rest of the app, never the legacy standalone entities-api);
 *  - **pipeline**  — at least one pipeline definition (`GET /pipelines`).
 *
 * Each probe asks for a single row (`limit=1`) and degrades gracefully — a
 * failing/empty probe just leaves its step "to do", never throwing. A
 * `localStorage` dismissal lets the operator hide the guide once they're set up
 * (or while a probe is wrong); `?guide=1` / the explicit `/getting-started`
 * route can still surface it. Probes are skipped while dismissed so the landing
 * page pays no network cost once the guide is gone.
 */
import * as React from "react";
import { useAddressInventory } from "@authe/web3";
import { useQuery } from "@authe/ui-core/api";
import { CatalogItemKind } from "@authe/catalog";
import { useCatalogApi } from "@/api/adapters";
import { ROUTES } from "@/config";

export type OnboardingStepId = "wallet" | "team" | "contracts" | "catalog" | "pipeline";

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  /** Route the step's CTA navigates to. */
  to: string;
  /** CTA label. */
  cta: string;
  /** Inferred completion. */
  done: boolean;
  /** The signal is still resolving. */
  loading: boolean;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  completedCount: number;
  total: number;
  allComplete: boolean;
  /** Any probe still resolving. */
  isLoading: boolean;
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
}

const DISMISS_KEY = "dashboard.onboarding.dismissed";

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/** True when a list payload carries at least one row (array / `items` / `results`). */
function hasAnyItem(data: unknown): boolean {
  if (Array.isArray(data)) return data.length > 0;
  if (data && typeof data === "object") {
    const obj = data as { items?: unknown; results?: unknown };
    if (Array.isArray(obj.items)) return obj.items.length > 0;
    if (Array.isArray(obj.results)) return obj.results.length > 0;
  }
  return false;
}

export function useOnboardingProgress(): OnboardingProgress {
  const [dismissed, setDismissed] = React.useState<boolean>(readDismissed);

  const dismiss = React.useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // storage unavailable — honour the choice for this session only
    }
  }, []);

  const restore = React.useCallback(() => {
    setDismissed(false);
    try {
      window.localStorage.removeItem(DISMISS_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Probes run only while the guide is live (not dismissed), so a satisfied /
  // hidden guide costs nothing on the landing page.
  const enabled = !dismissed;
  const { isConnected } = useAddressInventory();

  const teams = useListProbe("/teams", enabled);
  const abis = useListProbe("/abis", enabled);
  const entities = useCatalogProbe(enabled);
  const pipelines = useListProbe("/pipelines", enabled);

  const steps = React.useMemo<OnboardingStep[]>(
    () => [
      {
        id: "wallet",
        title: "Connect a wallet",
        description:
          "Link a browser wallet to surface your own on-chain activity and sign actions.",
        to: ROUTES.wallet,
        cta: "Connect wallet",
        done: isConnected,
        loading: false,
      },
      {
        id: "team",
        title: "Invite your team",
        description:
          "Add teammates so they can collaborate on investigations and back you up on the account.",
        to: ROUTES.team,
        cta: "Invite members",
        done: teams.done,
        loading: teams.loading,
      },
      {
        id: "contracts",
        title: "Upload contract ABIs",
        description:
          "Register ABIs so transactions touching your contracts decode into readable calls and events.",
        to: ROUTES.contracts,
        cta: "Add ABIs",
        done: abis.done,
        loading: abis.loading,
      },
      {
        id: "catalog",
        title: "Build your catalog",
        description:
          "Create tags, labels, entities and groups to attribute addresses and enrich every transaction view.",
        to: ROUTES.catalog,
        cta: "Open the catalog",
        done: entities.done,
        loading: entities.loading,
      },
      {
        id: "pipeline",
        title: "Create a pipeline",
        description:
          "Process your protocol's transactions your way — decode, enrich and route them through a pipeline.",
        to: ROUTES.pipelines,
        cta: "Build a pipeline",
        done: pipelines.done,
        loading: pipelines.loading,
      },
    ],
    [isConnected, teams, abis, entities, pipelines],
  );

  const completedCount = steps.filter((s) => s.done).length;
  const isLoading = steps.some((s) => s.loading);

  return {
    steps,
    completedCount,
    total: steps.length,
    allComplete: !isLoading && completedCount === steps.length,
    isLoading,
    dismissed,
    dismiss,
    restore,
  };
}

interface ProbeResult {
  done: boolean;
  loading: boolean;
}

/** One list probe: `GET {path}?limit=1` → `{ done: hasItems, loading }`. */
function useListProbe(path: string, enabled: boolean): ProbeResult {
  const query = useQuery<unknown>(
    (client, signal) => client.get<unknown>(path, { query: { limit: 1 }, signal }),
    { enabled, deps: [path, enabled] },
  );
  return React.useMemo(
    () => ({ done: hasAnyItem(query.data), loading: enabled && query.isLoading }),
    [query.data, query.isLoading, enabled],
  );
}

/**
 * Catalog step probe — asks catalog-mgmt for a single entity through the shared
 * {@link useCatalogApi} port (`list("entity", { limit: 1 })`), so it rides the
 * same GraphQL fast path + `entity → entities` routing as the rest of the app
 * instead of the legacy standalone entities-api. `CatalogApi.list` already
 * unwraps to a `CatalogRow[]`, so there's no envelope to feed `useQuery`; a tiny
 * effect drives the `{ done, loading }` result. Best-effort: a failing/empty
 * probe just leaves the step "to do" (never throws), and it's skipped while the
 * guide is dismissed so the landing page pays no network cost.
 */
function useCatalogProbe(enabled: boolean): ProbeResult {
  const api = useCatalogApi();
  const [result, setResult] = React.useState<ProbeResult>(() => ({ done: false, loading: enabled }));

  React.useEffect(() => {
    if (!enabled) {
      setResult({ done: false, loading: false });
      return;
    }
    let active = true;
    setResult((prev) => ({ done: prev.done, loading: true }));
    api
      .list(CatalogItemKind.ENTITY, { limit: 1 })
      .then((page) => {
        if (active) setResult({ done: page.items.length > 0, loading: false });
      })
      .catch(() => {
        if (active) setResult({ done: false, loading: false });
      });
    return () => {
      active = false;
    };
  }, [api, enabled]);

  return result;
}
